import { Component, ChangeDetectionStrategy, inject, OnInit, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, skip, take } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';

import { LibrariesFacade } from './libraries.facade';
import { BrowsePaginatorComponent } from '../../features/browse-index/components/browse-paginator/browse-paginator.component';
import { BreadCrump } from '../../shared/bread-crump/bread-crump';
import { LibrariesCard } from '../../shared/libraries-card/libraries-card';
import { SearchBar } from '../../shared/search-bar/search-bar';
import { FilterPanelComponent } from '../../features/browse-index/components/filter-panel/filter-panel.component';
import { SkeletonCardComponent } from '../../features/browse-index/components/skeleton-card/skeleton-card.component';
import { EmptyStateComponent } from '../../features/browse-index/components/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../features/browse-index/components/error-state/error-state.component';

@Component({
  selector: 'app-libraries',
  standalone: true,
  imports: [
    CommonModule, 
    BrowsePaginatorComponent, 
    BreadCrump, 
    LibrariesCard, 
    TranslateModule, 
    SearchBar,
    FilterPanelComponent,
    SkeletonCardComponent,
    EmptyStateComponent,
    ErrorStateComponent
  ],
  templateUrl: './libraries.html',
  styleUrl: './libraries.scss',
  providers: [LibrariesFacade],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Libraries implements OnInit {
  readonly facade = inject(LibrariesFacade);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  readonly items$ = this.facade.items$;
  readonly loading$ = this.facade.loading$;
  readonly error$ = this.facade.error$;
  readonly pagination$ = this.facade.pagination$;
  readonly activeFilters$ = this.facade.activeFilters$;
  readonly sortState$ = this.facade.sortState$;
  readonly searchQuery$ = this.facade.searchQuery$;
  readonly isEmpty$ = this.facade.isEmpty$;
  readonly filterGroups$ = this.facade.filterGroups$;

  isFilterPanelOpen = false;

  ngOnInit(): void {
    this.restoreStateFromUrl(); 
    this.syncStateToUrl();
    this.handleBrowserNavigation();
  }

  onSearchChange(term: string): void {
    this.facade.updateSearch(term);
  }

  onClearSearch(): void {
    this.facade.clearSearch();
  }

  toggleFilterPanel(): void {
    this.isFilterPanelOpen = !this.isFilterPanelOpen;
  }

  onFilterToggle(groupKey: string, value: string): void {
    this.facade.toggleFilter(groupKey, value);
  }

  onRemoveFilter(groupKey: string, value: string): void {
    this.facade.toggleFilter(groupKey, value);
  }

  onSearchFacet(event: { groupKey: string; term: string }): void {
    this.facade.searchFacet(event.groupKey, event.term);
  }

  onLoadMoreFacet(groupKey: string): void {
    this.facade.loadMoreFacet(groupKey);
  }

  onClearFilterGroup(groupKey: string): void {
    this.facade.clearFilter(groupKey);
  }

  onClearAllFilters(): void {
    this.facade.clearAllFilters();
  }

  onPageChange(page: number): void {
    this.facade.goToPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onPageSizeChange(size: number): void {
    this.facade.changePageSize(size);
  }

  onRetry(): void {
    this.facade.retry();
  }

  private restoreStateFromUrl(): void {
    this.route.queryParams.pipe(
      take(1),
    ).subscribe(params => {
      if (Object.keys(params).length > 0) {
        this.facade.restoreStateFromParams(params);
      }
    });
  }

  private syncStateToUrl(): void {
    this.facade.queryParamsSnapshot$.pipe(
      takeUntilDestroyed(this.destroyRef),
      skip(1),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    ).subscribe(params => {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: params,
        queryParamsHandling: 'replace',
        replaceUrl: true,
      });
    });
  }

  private handleBrowserNavigation(): void {
    this.route.queryParams.pipe(
      takeUntilDestroyed(this.destroyRef),
      skip(1),
    ).subscribe(params => {
      this.facade.restoreStateFromParams(params);
    });
  }
}
